/**
 * intake-form response PROJECTIONS — hand-composed from generated atoms.
 *
 * Covers the intake-forms surface: the form definition (list + entity), the
 * PUBLIC fill-in view a patient sees behind a token, the submission row shown
 * on a client profile, and the booking-gate "outstanding" shape.
 *
 * The generator types the two jsonb columns — `intakeForm.fields` and
 * `intakeSubmission.fieldsSnapshot` / `.answers` — as `z.unknown()` (it can't
 * see through `jsonb().$type<...>()`), so those are hand-modeled here to match
 * the DB shapes in packages/database/src/schema/intake-form.ts. Pure Zod.
 *
 * Follows the proof pattern in ./leads.ts. Each response exports a schema plus
 * its `z.infer` type.
 */
import { intakeFieldTypeValues } from '@borradh-workspace/labels';
import { z } from 'zod';
import { intakeFormAtomSchema, intakeSubmissionAtomSchema, } from '../generated/index.js';
// ============================================================================
// FIELD + ANSWER SHAPES (jsonb, hand-modeled)
// ============================================================================
/** One question in a form. Matches `IntakeFormField` in the DB schema. */
export const intakeFormFieldSchema = z.object({
    id: z.string(),
    type: z.enum(intakeFieldTypeValues),
    label: z.string(),
    required: z.boolean().optional(),
    options: z.array(z.string()).optional(),
    helpText: z.string().optional(),
});
/** A drawn signature answer. */
export const intakeSignatureAnswerSchema = z.object({
    dataUrl: z.string(),
    signedAt: z.string(),
});
/** A single answer. Shape follows the field type (see `IntakeAnswer`). */
export const intakeAnswerSchema = z.union([
    z.string(),
    z.array(z.string()),
    z.boolean(),
    intakeSignatureAnswerSchema,
]);
// ============================================================================
// INTAKE FORM (dashboard)
// ============================================================================
/** An intake form definition on the wire (jsonb `fields` typed). */
export const intakeFormSchema = intakeFormAtomSchema.extend({
    fields: z.array(intakeFormFieldSchema),
});
/** `GET /intake-forms` — the `{ items }` wrapper. */
export const listIntakeFormsResponseSchema = z.object({
    items: z.array(intakeFormSchema),
});
/** `POST /intake-forms/seed-templates`. */
export const seedIntakeTemplatesResponseSchema = z.object({
    created: z.array(z.string()),
    skipped: z.array(z.string()),
});
/** `PUT /intake-forms/services/:serviceId/forms`. */
export const setServiceIntakeFormsResponseSchema = z.object({
    serviceId: z.string(),
    count: z.number(),
});
/**
 * `GET /intake-forms/services/:serviceId/forms` — the forms currently linked to
 * a service, so the attach dialog opens pre-populated.
 */
export const getServiceIntakeFormsResponseSchema = z.object({
    forms: z.array(z.object({
        intakeFormId: z.string(),
        intakeFormName: z.string(),
        blocksBooking: z.boolean(),
    })),
});
/** `POST /intake-forms/issue` — mints a send link, returns the RAW token. */
export const issueIntakeSubmissionResponseSchema = z.object({
    submissionId: z.string(),
    token: z.string(),
});
// ============================================================================
// INTAKE SUBMISSION (client profile)
// ============================================================================
/**
 * A submission row on a client profile — the snapshot `fieldsSnapshot` and
 * `answers` jsonb are typed. `tokenHash` is deliberately NOT projected here so
 * a client-profile read never carries the credential hash to the browser.
 */
export const intakeSubmissionSchema = intakeSubmissionAtomSchema
    .omit({ tokenHash: true })
    .extend({
    fieldsSnapshot: z.array(intakeFormFieldSchema),
    answers: z.record(z.string(), intakeAnswerSchema),
});
/** `GET /intake-forms/submissions/lead/:leadId` — the `{ items }` wrapper. */
export const listLeadSubmissionsResponseSchema = z.object({
    items: z.array(intakeSubmissionSchema),
});
// ============================================================================
// OUTSTANDING (booking gate)
// ============================================================================
/** `GET /intake-forms/outstanding/:appointmentId`. */
export const outstandingIntakeSchema = z.object({
    blockingCount: z.number(),
    pendingCount: z.number(),
    isBlocked: z.boolean(),
});
// ============================================================================
// PUBLIC INTAKE VIEW (the form to fill)
// ============================================================================
/** Completed bearer links expose only a receipt and public clinic details. */
const publicIntakeOrganizationSchema = z.object({
    name: z.string(),
    slug: z.string(),
    logo: z.string().nullable(),
});
export const publicIntakeViewSchema = z.discriminatedUnion('status', [
    z
        .object({
        status: z.literal('completed'),
        organization: publicIntakeOrganizationSchema,
    })
        .strict(),
    z
        .object({
        status: z.literal('pending'),
        submissionId: z.string(),
        formName: z.string(),
        formDescription: z.string().nullable(),
        fields: z.array(intakeFormFieldSchema),
        organization: publicIntakeOrganizationSchema,
    })
        .strict(),
]);
/** `POST /public/intake/:organizationSlug/:token/submit`. */
export const submitIntakeFormResponseSchema = z.object({
    submissionId: z.string(),
});
//# sourceMappingURL=intake.js.map